/**
 * How a clinic invites patients to check in: a QR code for the waiting room wall,
 * and the same link as text to send however it already reaches its patients.
 *
 * A dialog rather than a page: it is a thing you fetch once, print, and stick up.
 * Giving it a route meant a screen a clinic visits twice a year sitting in the
 * nav's way every day.
 *
 * Only reachable once check-ins are switched on. It used to carry a warning for
 * the switched-off case; the page no longer offers this dialog at all until then,
 * which is the better answer to the same problem.
 */

import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { buildVisitStartUrl } from "services/clinicService";

export default function VisitStartInviteDialog({
  open,
  onClose,
  clinicId,
  clinicName,
}) {
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const url = clinicId ? buildVisitStartUrl(clinicId) : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[VisitStartInviteDialog] copy failed:", err);
    }
  };

  /**
   * Printed from a popup window with its own styles rather than the dashboard's:
   * the QR is already an inline SVG, so it serialises straight in, and nothing
   * here has to fight the app's layout for a sheet of paper.
   *
   * The URL is printed as text under the code deliberately. A scan that fails in
   * a waiting room — a scratched lens, an older phone, someone who does not know
   * their camera reads QR codes — is the case a poster has to survive, and a
   * typeable address is the only thing that helps.
   */
  const handlePrint = useCallback(() => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) return;
    const esc = (v) =>
      String(v).replace(/[&<>"']/g, (c) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
      })[c]);
    win.document.write(`<!doctype html><html><head><title>VisitStart code</title>
      <style>
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; }
        .wrap { min-height: 100vh; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 28px;
          padding: 56px; text-align: center; box-sizing: border-box; }
        h1 { font-size: 44px; line-height: 1.1; margin: 0; }
        h2 { font-size: 24px; font-weight: normal; margin: 0; color: #444; }
        .qr { padding: 28px; }
        .url { font-size: 20px; color: #333; word-break: break-all; }
        .hint { font-size: 17px; color: #555; max-width: 30em; line-height: 1.5; }
        @media print { .wrap { padding: 40px; } }
      </style></head>
      <body><div class="wrap">
        <h1>Save time at the desk</h1>
        <h2>${esc(clinicName)}</h2>
        <div class="qr">${svg.outerHTML}</div>
        <p class="hint">Scan this with your phone camera to answer a few questions
          before you are called. You do not need an account.</p>
        <p class="url">${esc(url)}</p>
      </div></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }, [clinicName, url]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invite patients</DialogTitle>
      <DialogContent>
        <Stack spacing={3} alignItems="center" sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Print this and put it up where patients wait, or send them the link.
            Anyone who opens it can answer your questions before they are called,
            with or without an account.
          </Typography>

          {/* The white padding IS the QR quiet zone: qrcode.react paints white
              behind the modules but adds no margin, and a code butted against
              surrounding content is the classic first-scan failure. lineHeight 0
              stops the inline SVG adding a descender gap under itself. */}
          <Box
            ref={qrRef}
            sx={{ p: 2.5, bgcolor: "common.white", borderRadius: 2, lineHeight: 0 }}
          >
            <QRCodeSVG value={url} size={200} level="H" />
          </Box>

          <TextField
            label="Link"
            value={url}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? "Copied" : "Copy link"}>
                    <IconButton onClick={handleCopy} edge="end">
                      <ContentCopyRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button onClick={handlePrint}>Print</Button>
      </DialogActions>
    </Dialog>
  );
}

VisitStartInviteDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  clinicId: PropTypes.string,
  clinicName: PropTypes.string,
};
